import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { UserRole } from "../../types/auth/auth.types";
import { Loading } from "../ui/loading";

// Props for the ProtectedRoute component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  allowedRoles?: UserRole[];
}

/**
 * ProtectedRoute component that handles authentication-based routing.
 * Redirects users based on their authentication state, claims, and the route's requirements.
 *
 * @example
 * <ProtectedRoute requireAuth={true} allowedRoles={['admin']}>
 *   <AdminDashboard />
 * </ProtectedRoute>
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAuth = true, allowedRoles }) => {
  const { user, loading, claims, claimsLoading } = useAuth();
  const location = useLocation();

  // Show loading state when either the auth or claims are loading
  if (loading || (requireAuth && user && claimsLoading)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loading size="lg" color="border-teal-500" />
      </div>
    );
  }

  if (requireAuth && !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!requireAuth && user) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && (!claims?.role || !allowedRoles.includes(claims.role))) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
